import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const TABS = ['BUY', 'SELL', 'TRADE/SWAP', 'EPA COMPLIANCE'];
const CREDIT_TYPES = ['Mandatory CCC - CCTS Compliance', 'Voluntary CCC - Offset Mechanism', 'Renewable Energy Credits (REC)', 'CDM Certified Credits', 'Article 6.4 Paris Credits'];
const PROJECT_CATEGORIES = ['Renewable Energy', 'Agroforestry & Forestry', 'Waste Management', 'CCUS (Carbon Capture)', 'Sustainable Agriculture', 'Energy Efficiency', 'Other'];
const PROJECT_STATUSES = ['Idea / Early Stage', 'Validation Ongoing', 'Registered with BEE', 'Verified by ACVA', 'Issuing Credits'];
const REGISTRIES = ['GCI National Registry', 'VERRA (VCS)', 'Gold Standard', 'CDM (UNFCCC)', 'Other'];
const SECTORS = ['Iron & Steel', 'Cement', 'Aluminium', 'Chlor-Alkali', 'Fertiliser', 'Pulp & Paper', 'Petrochemicals', 'Petroleum Refinery', 'Textiles'];
const TAB_COLORS = ['var(--neon-green)', 'var(--neon-pink)', 'var(--neon-cyan)', 'var(--neon-orange)'];

function OrderBook({ price, source }) {
  const [book, setBook] = useState({ asks: [], bids: [], spread: 0, book_type: 'mixed_reference' });

  useEffect(() => {
    const fetchBook = () => api.get('/market/orderbook').then((r) => setBook(r.data)).catch(() => {});
    fetchBook();
    const intervalId = setInterval(fetchBook, 15000);
    return () => clearInterval(intervalId);
  }, []);

  return (
    <div className="panel-stack">
      <div className="section-header-row">
        <div className="section-stack" style={{ gap: '0.3rem' }}>
          <div className="section-tag">Order book</div>
          <div className="headline-sm">Reference depth and active listing asks</div>
        </div>
        <div className="live-indicator"><div className="live-dot" />Reference</div>
      </div>

      <div className="copy-sm">
        Active asks are pulled from platform listings. Bid depth remains reference pricing until an official public exchange feed is available.
        {source?.label ? ` Source: ${source.label}.` : ''}
      </div>

      <div className="trade-book">
        <div className="trade-book-header">
          <span>Price</span>
          <span>Qty</span>
          <span>Total</span>
        </div>

        {book.asks.slice().reverse().map((ask, index) => (
          <div key={`ask-${index}`} className="trade-book-row">
            <div style={{ position: 'absolute', inset: '2px 0', width: `${Math.min(92, ask.qty / 6)}%`, background: 'rgba(255,0,153,0.06)', borderRadius: '12px' }} />
            <span style={{ color: 'var(--neon-pink)' }}>Rs.{ask.price.toLocaleString('en-IN')}</span>
            <span>{ask.qty}</span>
            <span style={{ color: 'var(--muted2)' }}>Rs.{(ask.price * ask.qty).toLocaleString('en-IN', { notation: 'compact' })}</span>
          </div>
        ))}

        <div className="book-spread">
          Spread Rs.{book.spread?.toFixed(2)} | {((book.spread / (price || 2840)) * 100).toFixed(2)}%
        </div>

        {book.bids.map((bid, index) => (
          <div key={`bid-${index}`} className="trade-book-row">
            <div style={{ position: 'absolute', inset: '2px 0', width: `${Math.min(92, bid.qty / 6)}%`, background: 'rgba(0,255,136,0.06)', borderRadius: '12px' }} />
            <span style={{ color: 'var(--neon-green)' }}>Rs.{bid.price.toLocaleString('en-IN')}</span>
            <span>{bid.qty}</span>
            <span style={{ color: 'var(--muted2)' }}>Rs.{(bid.price * bid.qty).toLocaleString('en-IN', { notation: 'compact' })}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Trade() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [market, setMarket] = useState(null);
  const [price, setPrice] = useState(2840);
  const [listings, setListings] = useState([]);

  const [submitting, setSubmitting] = useState(false);
  const [buy, setBuy] = useState({ listing_id: '', qty: 100, credit_type: CREDIT_TYPES[0], max_price: 2900 });
  const [sell, setSell] = useState({ credit_type: CREDIT_TYPES[1], qty: 100, price_per_unit: 2840, project: '', vintage: '2025-26', verified_by: 'BEE Accredited Agency', description: '', project_category: PROJECT_CATEGORIES[0], project_status: PROJECT_STATUSES[0], registry: REGISTRIES[0], estimated_annual_co2e: '' });
  const [epa, setEpa] = useState({ qty: 1000, credit_type: 'Mandatory CCC - CCTS Compliance', compliance_year: 'FY 2025-26', gci_registry_id: '', sector: SECTORS[0] });
  const [swap, setSwap] = useState({ offer_type: 'Voluntary', offer_qty: 100, want_type: 'Mandatory', trade_ratio: 1.05, trade_mode: 'Spot (Immediate Settlement)' });

  useEffect(() => {
    const fetchMarket = () => api.get('/market/prices').then((r) => {
      setMarket(r.data);
      setPrice(r.data.spot.ccc_mandatory);
    }).catch(() => {});

    fetchMarket();
    api.get('/listings').then((r) => setListings(r.data.listings || [])).catch(() => {});
    const intervalId = setInterval(fetchMarket, 30000);
    return () => clearInterval(intervalId);
  }, []);

  const handleBuy = async () => {
    if (!buy.listing_id) return toast.error('Please select a listing');
    if (buy.qty <= 0) return toast.error('Quantity must be greater than 0');
    try {
      setSubmitting(true);
      await api.post('/trades/buy', { listing_id: buy.listing_id, qty: buy.qty });
      toast.success('Purchase successful!');
      navigate('/transactions');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Purchase failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSell = async () => {
    if (!sell.project.trim()) return toast.error('Project name is required');
    if (sell.qty <= 0) return toast.error('Quantity must be greater than 0');
    if (sell.price_per_unit <= 0) return toast.error('Price must be greater than 0');
    try {
      setSubmitting(true);
      await api.post('/listings', sell);
      toast.success('Listing created!');
      navigate('/my-listings');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create listing');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSwap = async () => {
    if (swap.offer_qty <= 0) return toast.error('Offer quantity must be greater than 0');
    if (swap.offer_type === swap.want_type) return toast.error('Offer and want types must be different');
    try {
      setSubmitting(true);
      await api.post('/trades/swap', { offer_type: swap.offer_type, offer_qty: swap.offer_qty, want_type: swap.want_type, trade_ratio: swap.trade_ratio, trade_mode: swap.trade_mode });
      toast.success('Swap initiated!');
      navigate('/transactions');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Swap failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEpa = async () => {
    if (epa.qty <= 0) return toast.error('Credits to surrender must be greater than 0');
    if (!epa.gci_registry_id.trim()) return toast.error('GCI registry ID is required');
    try {
      setSubmitting(true);
      await api.post('/trades/epa-surrender', { qty: epa.qty, credit_type: epa.credit_type, compliance_year: epa.compliance_year, gci_registry_id: epa.gci_registry_id, sector: epa.sector });
      toast.success('Credits surrendered!');
      navigate('/transactions');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Surrender failed');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedListing = listings.find((listing) => listing.id === buy.listing_id);
  const buyUnitPrice = selectedListing?.price_per_unit || price;
  const buyTotal = buy.qty * buyUnitPrice;
  const fee = Math.round(buyTotal * 0.003);
  const compensationMultiplier = market?.compliance?.environmental_compensation_multiplier || 2;
  const compliancePrice = market?.compliance?.average_traded_price_inr || price || 2840;
  const estimatedCompensation = Math.round(epa.qty * compliancePrice * compensationMultiplier);
  const userBalance = user?.balance || 0;
  const carbonBalance = user?.carbon_balance || 0;

  const topMetrics = useMemo(() => ([
    { label: 'Reference price', value: `Rs.${price.toLocaleString('en-IN')}`, copy: market?.source?.label || 'Reference market snapshot', color: 'var(--neon-green)' },
    { label: 'Wallet balance', value: `Rs.${userBalance.toLocaleString('en-IN')}`, copy: 'Available for buying and settlement', color: 'var(--neon-cyan)' },
    { label: 'Carbon inventory', value: `${carbonBalance.toLocaleString('en-IN')} tCO2e`, copy: 'Available for listing or swap', color: 'var(--neon-orange)' },
    { label: '24h change', value: `${market?.change_24h?.ccc_mandatory >= 0 ? '+' : '-'}${Math.abs(market?.change_24h?.ccc_mandatory || 0).toFixed(1)}%`, copy: 'Reference movement', color: 'var(--neon-pink)' },
  ]), [carbonBalance, market, price, userBalance]);

  const renderBuy = () => (
    <div className="panel-stack">
      <div className="surface-muted" style={{ padding: '1rem' }}>
        <div className="eyebrow">Buy credits</div>
        <div className="headline-sm" style={{ marginTop: '0.45rem' }}>Pick a listing and confirm the wallet impact before execution.</div>
      </div>

      <div className="form-group">
        <label className="form-label">Select listing</label>
        <select className="form-select" value={buy.listing_id} onChange={(e) => setBuy({ ...buy, listing_id: e.target.value })}>
          <option value="">Select a listing</option>
          {listings.map((listing) => (
            <option key={listing.id} value={listing.id}>
              {(listing.seller_name || listing.title || listing.project)} - Rs.{listing.price_per_unit} - {listing.qty_available} units
            </option>
          ))}
        </select>
      </div>

      <div className="detail-grid">
        <div className="form-group">
          <label className="form-label">Credit type</label>
          <select className="form-select" value={buy.credit_type} onChange={(e) => setBuy({ ...buy, credit_type: e.target.value })}>
            {CREDIT_TYPES.map((item) => <option key={item}>{item}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Quantity</label>
          <input className="form-input" type="number" min="1" value={buy.qty} onChange={(e) => setBuy({ ...buy, qty: Number(e.target.value) })} />
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Max price per credit</label>
        <input className="form-input" type="number" value={buy.max_price} onChange={(e) => setBuy({ ...buy, max_price: Number(e.target.value) })} />
      </div>

      <div className="surface-muted" style={{ padding: '1rem' }}>
        <div className="summary-list">
          <div className="summary-row"><span className="label">Selected ask</span><span className="value" style={{ color: 'var(--neon-green)' }}>Rs.{buyUnitPrice.toLocaleString('en-IN')}</span></div>
          <div className="summary-row"><span className="label">Estimated total</span><span className="value">Rs.{buyTotal.toLocaleString('en-IN')}</span></div>
          <div className="summary-row"><span className="label">Platform fee</span><span className="value">Rs.{fee.toLocaleString('en-IN')}</span></div>
          <div className="summary-row"><span className="label">Net payable</span><span className="value" style={{ color: 'var(--neon-green)' }}>Rs.{(buyTotal + fee).toLocaleString('en-IN')}</span></div>
          <div className="soft-divider" />
          <div className="summary-row"><span className="label">Wallet balance</span><span className="value" style={{ color: userBalance >= buyTotal + fee ? 'var(--neon-green)' : 'var(--neon-pink)' }}>Rs.{userBalance.toLocaleString('en-IN')}</span></div>
        </div>
      </div>

      <button
        className="btn btn-primary"
        style={{ width: '100%', justifyContent: 'center' }}
        onClick={handleBuy}
        disabled={submitting}
      >
        {submitting ? 'Processing...' : 'Buy credits'}
      </button>
    </div>
  );

  const renderSell = () => (
    <div className="panel-stack">
      <div className="surface-muted" style={{ padding: '1rem' }}>
        <div className="eyebrow">Sell credits</div>
        <div className="headline-sm" style={{ marginTop: '0.45rem' }}>List inventory with cleaner project and pricing details.</div>
      </div>

      <div className="pills-row">
        <span className="badge badge-pink">Available {carbonBalance} tCO2e</span>
        <span className="badge badge-muted">Credits are reserved in escrow after listing</span>
      </div>

      <div className="form-group">
        <label className="form-label">Credit type</label>
        <select className="form-select" value={sell.credit_type} onChange={(e) => setSell({ ...sell, credit_type: e.target.value })}>
          {CREDIT_TYPES.map((item) => <option key={item}>{item}</option>)}
        </select>
      </div>

      <div className="form-group">
        <label className="form-label">Project / source</label>
        <input className="form-input" type="text" value={sell.project} onChange={(e) => setSell({ ...sell, project: e.target.value })} placeholder="Solar Farm, Rajasthan" />
      </div>

      <div className="detail-grid">
        <div className="form-group">
          <label className="form-label">Quantity</label>
          <input className="form-input" type="number" value={sell.qty} onChange={(e) => setSell({ ...sell, qty: Number(e.target.value) })} />
        </div>
        <div className="form-group">
          <label className="form-label">Ask price</label>
          <input className="form-input" type="number" value={sell.price_per_unit} onChange={(e) => setSell({ ...sell, price_per_unit: Number(e.target.value) })} />
        </div>
      </div>

      <div className="detail-grid">
        <div className="form-group">
          <label className="form-label">Vintage</label>
          <select className="form-select" value={sell.vintage} onChange={(e) => setSell({ ...sell, vintage: e.target.value })}>
            <option>2025-26</option>
            <option>2024-25</option>
            <option>2023-24</option>
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Verified by</label>
          <select className="form-select" value={sell.verified_by} onChange={(e) => setSell({ ...sell, verified_by: e.target.value })}>
            <option>BEE Accredited Agency</option>
            <option>VERRA (VCS)</option>
            <option>Gold Standard</option>
            <option>GCI Registry</option>
          </select>
        </div>
      </div>

      <div className="detail-grid">
        <div className="form-group">
          <label className="form-label">Project category</label>
          <select className="form-select" value={sell.project_category} onChange={(e) => setSell({ ...sell, project_category: e.target.value })}>
            {PROJECT_CATEGORIES.map((item) => <option key={item}>{item}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Project status</label>
          <select className="form-select" value={sell.project_status} onChange={(e) => setSell({ ...sell, project_status: e.target.value })}>
            {PROJECT_STATUSES.map((item) => <option key={item}>{item}</option>)}
          </select>
        </div>
      </div>

      <div className="detail-grid">
        <div className="form-group">
          <label className="form-label">Registry</label>
          <select className="form-select" value={sell.registry} onChange={(e) => setSell({ ...sell, registry: e.target.value })}>
            {REGISTRIES.map((item) => <option key={item}>{item}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Est. annual CO₂e (tCO₂e/yr)</label>
          <input className="form-input" type="text" value={sell.estimated_annual_co2e} onChange={(e) => setSell({ ...sell, estimated_annual_co2e: e.target.value })} placeholder="e.g. 2000" />
        </div>
      </div>

      <button
        className="btn btn-danger"
        style={{ width: '100%', justifyContent: 'center' }}
        onClick={handleSell}
        disabled={submitting}
      >
        {submitting ? 'Processing...' : 'List credits'}
      </button>
    </div>
  );

  const renderSwap = () => (
    <div className="panel-stack">
      <div className="surface-muted" style={{ padding: '1rem' }}>
        <div className="eyebrow">Trade and swap</div>
        <div className="headline-sm" style={{ marginTop: '0.45rem' }}>Swap credit classes with a clearer settlement summary.</div>
        <div className="copy-sm" style={{ marginTop: '0.55rem' }}>
          Final exchange connectivity and compliance routing still need separate production integrations.
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">You offer</label>
        <select className="form-select" value={swap.offer_type} onChange={(e) => setSwap({ ...swap, offer_type: e.target.value })}>
          <option value="Voluntary">Voluntary CCC (Offset Mechanism)</option>
          <option value="Mandatory">Mandatory CCC (CCTS)</option>
          <option value="REC">Renewable Energy Credits</option>
        </select>
      </div>

      <div className="detail-grid">
        <div className="form-group">
          <label className="form-label">Offer quantity</label>
          <input className="form-input" type="number" value={swap.offer_qty} onChange={(e) => setSwap({ ...swap, offer_qty: Number(e.target.value) })} />
        </div>
        <div className="form-group">
          <label className="form-label">Trade ratio</label>
          <input className="form-input" type="number" step="0.01" value={swap.trade_ratio} onChange={(e) => setSwap({ ...swap, trade_ratio: Number(e.target.value) })} />
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">You want</label>
        <select className="form-select" value={swap.want_type} onChange={(e) => setSwap({ ...swap, want_type: e.target.value })}>
          <option value="Mandatory">Mandatory CCC (CCTS Compliance)</option>
          <option value="Voluntary">Voluntary CCC (Offset)</option>
          <option value="Paris">Article 6.4 Paris Credits</option>
        </select>
      </div>

      <div className="form-group">
        <label className="form-label">Settlement mode</label>
        <select className="form-select" value={swap.trade_mode} onChange={(e) => setSwap({ ...swap, trade_mode: e.target.value })}>
          <option>Spot (Immediate Settlement)</option>
          <option>Forward Contract (30 days)</option>
          <option>Forward Contract (90 days)</option>
          <option>P2P Bilateral Agreement</option>
        </select>
      </div>

      <div className="surface-muted" style={{ padding: '1rem' }}>
        <div className="summary-row"><span className="label">Approximate receive quantity</span><span className="value" style={{ color: 'var(--neon-cyan)' }}>{Math.floor(swap.offer_qty / swap.trade_ratio)} {swap.want_type}</span></div>
      </div>

      <button
        className="btn btn-secondary"
        style={{ width: '100%', justifyContent: 'center', borderColor: 'rgba(0,229,255,0.28)', color: 'var(--neon-cyan)' }}
        onClick={handleSwap}
        disabled={submitting}
      >
        {submitting ? 'Processing...' : 'Initiate swap'}
      </button>
    </div>
  );

  const renderCompliance = () => (
    <div className="panel-stack">
      <div className="surface-muted" style={{ padding: '1rem', borderColor: 'rgba(255,102,0,0.18)' }}>
        <div className="eyebrow" style={{ color: 'var(--neon-orange)' }}>EPA compliance</div>
        <div className="headline-sm" style={{ marginTop: '0.45rem' }}>Model surrender and shortfall exposure with current rule assumptions.</div>
        <div className="copy-sm" style={{ marginTop: '0.55rem' }}>
          Current modeled position uses {compensationMultiplier}x average traded CCC price as the shortfall reference amount. Confirm the latest notified rule and exchange average before filing.
        </div>
      </div>

      <div className="detail-grid">
        <div className="form-group">
          <label className="form-label">Sector</label>
          <select className="form-select" value={epa.sector} onChange={(e) => setEpa({ ...epa, sector: e.target.value })}>
            {SECTORS.map((item) => <option key={item}>{item}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Compliance year</label>
          <select className="form-select" value={epa.compliance_year} onChange={(e) => setEpa({ ...epa, compliance_year: e.target.value })}>
            <option>FY 2025-26</option>
            <option>FY 2026-27</option>
          </select>
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Credits to surrender</label>
        <input className="form-input" type="number" value={epa.qty} onChange={(e) => setEpa({ ...epa, qty: Number(e.target.value) })} />
      </div>

      <div className="form-group">
        <label className="form-label">GCI registry ID</label>
        <input className="form-input" type="text" value={epa.gci_registry_id} onChange={(e) => setEpa({ ...epa, gci_registry_id: e.target.value })} placeholder="GCI-2025-XXXXXX" />
      </div>

      <div className="surface-muted" style={{ padding: '1rem' }}>
        <div className="summary-list">
          <div className="summary-row"><span className="label">Credits to surrender</span><span className="value" style={{ color: 'var(--neon-orange)' }}>{epa.qty} CCCs</span></div>
          <div className="summary-row"><span className="label">Reference avg traded price</span><span className="value">Rs.{compliancePrice.toLocaleString('en-IN')}</span></div>
          <div className="summary-row"><span className="label">Compensation reference</span><span className="value" style={{ color: 'var(--neon-pink)' }}>Rs.{estimatedCompensation.toLocaleString('en-IN')} ({compensationMultiplier}x)</span></div>
          <div className="summary-row"><span className="label">Registry</span><span className="value">GCI National Registry</span></div>
        </div>
      </div>

      <button
        className="btn btn-orange"
        style={{ width: '100%', justifyContent: 'center' }}
        onClick={handleEpa}
        disabled={submitting}
      >
        {submitting ? 'Processing...' : 'Surrender credits'}
      </button>
    </div>
  );

  return (
    <div className="page-shell page-stack">
      <section className="surface-panel glow-surface" style={{ padding: '1.8rem' }}>
        <div className="section-stack">
          <div className="section-tag">Trading desk</div>
          <h1 className="section-title">A lighter, faster trade desk for buying, listing, swapping, and compliance</h1>
          <p className="section-copy">
            This screen now follows a more consumer-grade rhythm inspired by modern marketplace products: cleaner hierarchy, clearer summaries, and easier action paths while keeping the carbon-market workflow intact.
          </p>
        </div>

        <div className="hero-search-shell">
          <span className="hero-search-chip">Market source</span>
          <input className="hero-search-input" value={market?.source?.label || 'Reference market snapshot'} readOnly />
          <span className="hero-search-chip">KYC {user?.kyc_status || 'pending'}</span>
        </div>
      </section>

      {/* Non-dismissible simulation disclaimer — required on all trading screens */}
      <div style={{ background: 'rgba(255,238,0,0.07)', borderLeft: '3px solid var(--neon-yellow)', padding: '0.85rem 1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
        <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.7rem', color: 'var(--neon-yellow)', letterSpacing: '0.5px' }}>
          ⚠ SIMULATION ONLY — No real carbon credits are traded here. All trades are educational simulations. This is not investment advice. CarbonX is not a SEBI/CERC-registered broker or exchange.
        </div>
        <a href="/terms" style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.65rem', color: 'var(--muted)', textDecoration: 'underline', whiteSpace: 'nowrap' }}>Learn more</a>
      </div>

      {user?.kyc_status !== 'verified' && (
        <div className="notice-banner" style={{ marginTop: '0' }}>
          <div>KYC verification is required before trade execution or wallet settlement.</div>
          <a href="/kyc" className="btn btn-secondary btn-sm">Complete KYC</a>
        </div>
      )}

      <section className="metric-strip">
        {topMetrics.map((item) => (
          <div key={item.label} className="metric-tile">
            <div className="eyebrow">{item.label}</div>
            <div className="value" style={{ color: item.color }}>{item.value}</div>
            <div className="copy-sm" style={{ marginTop: '0.35rem' }}>{item.copy}</div>
          </div>
        ))}
      </section>

      <section className="trade-grid">
        <div className="surface-panel" style={{ padding: '1.5rem' }}>
          <div className="panel-stack">
            <div className="tab-switcher">
              {TABS.map((item, index) => (
                <button
                  key={item}
                  type="button"
                  className={`tab-pill ${tab === index ? 'active' : ''}`}
                  style={tab === index ? { borderColor: TAB_COLORS[index], color: TAB_COLORS[index], background: 'rgba(255,255,255,0.04)' } : undefined}
                  onClick={() => setTab(index)}
                >
                  {item}
                </button>
              ))}
            </div>

            {tab === 0 && renderBuy()}
            {tab === 1 && renderSell()}
            {tab === 2 && renderSwap()}
            {tab === 3 && renderCompliance()}
          </div>
        </div>

        <div className="panel-stack">
          <div className="surface-panel" style={{ padding: '1.5rem' }}>
            <OrderBook price={price} source={market?.source} />
          </div>

          <div className="surface-panel" style={{ padding: '1.4rem' }}>
            <div className="section-stack" style={{ gap: '0.55rem' }}>
              <div className="section-tag">Desk notes</div>
              <div className="headline-sm">Execution context</div>
              <div className="copy-sm">Buys and sells operate against platform listings and wallet balances. Market reference numbers are not presented as live exchange execution prices.</div>
              <div className="pills-row" style={{ marginTop: '0.3rem' }}>
                <span className="badge badge-green">Buying</span>
                <span className="badge badge-cyan">Swaps</span>
                <span className="badge badge-orange">Compliance</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
